import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { token_api } from "../../Utils/Network";

const cardSx = {
  background: "#fff",
  borderRadius: "14px",
  boxShadow: "0 1px 3px rgba(16,24,40,.06), 0 1px 2px rgba(16,24,40,.08)",
  border: "1px solid #eef0f4",
  p: { xs: 2, md: 3 },
  mb: 3,
};

const emptyConcern = {
  counselling_concerns_category: "",
  concern: "",
  name_for_report: "",
  description: "",
  practical_strategies: "",
  index: 1,
  for_report: true,
};

const unwrap = (res) =>
  res?.data?.data?.results || res?.data?.data || res?.data?.results || res?.data || [];

/**
 * The concerns a student can have and the text each one prints in the
 * report: its name, why it matters, and the practical strategies.
 */
const Concerns = () => {
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(emptyConcern);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newCategory, setNewCategory] = useState("");

  const fetchCategories = () => {
    token_api
      .get("counseling/v1/counselling_concerns/")
      .then((res) => setCategories(unwrap(res)))
      .catch((err) => console.error("Failed to load concerns:", err));
  };

  useEffect(fetchCategories, []);

  const addCategory = () => {
    const name = newCategory.trim();
    if (!name) return;
    token_api
      .post("counseling/v1/counselling_concerns/", { category: name })
      .then(() => {
        setNewCategory("");
        fetchCategories();
      })
      .catch((err) => {
        console.error("Add category error:", err?.response?.data || err);
        alert("Failed to add the category.");
      });
  };

  const deleteCategory = (cat) => {
    const count = (cat.concerns || []).length;
    const warning = count ? ` Its ${count} concern(s) will be deleted too.` : "";
    if (!window.confirm(`Delete category "${cat.category}"?${warning}`)) return;
    token_api
      .delete(`counseling/v1/counselling_concerns/${cat.id}/`)
      .then(fetchCategories)
      .catch((err) => {
        console.error("Delete category error:", err?.response?.data || err);
        alert("Failed to delete the category.");
      });
  };

  const saveConcern = () => {
    if (!form.counselling_concerns_category || !form.concern || !form.name_for_report) {
      alert("Category, concern and name for report are required.");
      return;
    }
    setSaving(true);
    const payload = { ...form, index: Number(form.index) || 1 };
    const request = editingId
      ? token_api.patch(`counseling/v1/counselling_concern_items/${editingId}/`, payload)
      : token_api.post("counseling/v1/counselling_concern_items/", payload);
    request
      .then(() => {
        cancelEdit();
        fetchCategories();
      })
      .catch((err) => {
        console.error("Save concern error:", err?.response?.data || err);
        alert("Failed to save the concern.");
      })
      .finally(() => setSaving(false));
  };

  const editConcern = (c) => {
    setEditingId(c.id);
    setForm({
      counselling_concerns_category: c.counselling_concerns_category,
      concern: c.concern || "",
      name_for_report: c.name_for_report || "",
      description: c.description || "",
      practical_strategies: c.practical_strategies || "",
      index: c.index ?? 1,
      for_report: !!c.for_report,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteConcern = (c) => {
    if (!window.confirm(`Delete concern "${c.concern}"?`)) return;
    token_api
      .delete(`counseling/v1/counselling_concern_items/${c.id}/`)
      .then(() => {
        if (editingId === c.id) cancelEdit();
        fetchCategories();
      })
      .catch((err) => {
        console.error("Delete concern error:", err?.response?.data || err);
        alert("Failed to delete the concern.");
      });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyConcern);
  };

  return (
    <div style={{ background: "#f5f7fb", minHeight: "100%", padding: "24px 0" }}>
      <div style={{ maxWidth: 1120, margin: "0 auto", padding: "0 16px" }}>
        <Box sx={cardSx}>
          <h2 style={{ color: "#313E6C", margin: "0 0 4px" }}>
            {editingId ? "Edit Concern" : "Add Concern"}
          </h2>
          <p style={{ color: "#696984", margin: "0 0 20px", fontSize: 14 }}>
            What you write here is what the student's report prints for this
            concern.
          </p>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                select
                label="Category *"
                size="small"
                fullWidth
                value={form.counselling_concerns_category}
                onChange={(e) =>
                  setForm({ ...form, counselling_concerns_category: e.target.value })
                }
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.category}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Concern *"
                size="small"
                fullWidth
                helperText="As the counsellor / student sees it"
                value={form.concern}
                onChange={(e) => setForm({ ...form, concern: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Name for Report *"
                size="small"
                fullWidth
                helperText="As printed in the PDF"
                value={form.name_for_report}
                onChange={(e) => setForm({ ...form, name_for_report: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                label="Order"
                type="number"
                size="small"
                fullWidth
                value={form.index}
                onChange={(e) => setForm({ ...form, index: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <label style={{ display: "flex", alignItems: "center", fontSize: 14 }}>
                <Checkbox
                  checked={form.for_report}
                  onChange={(e) => setForm({ ...form, for_report: e.target.checked })}
                />
                Show in report
              </label>
            </Grid>
            <Grid item xs={12} md={6}>
              <p style={{ margin: "0 0 6px", fontSize: 13, color: "#696984" }}>
                Why this matters (description)
              </p>
              <ReactQuill
                theme="snow"
                value={form.description}
                onChange={(value) => setForm((f) => ({ ...f, description: value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <p style={{ margin: "0 0 6px", fontSize: 13, color: "#696984" }}>
                Practical strategies
              </p>
              <ReactQuill
                theme="snow"
                value={form.practical_strategies}
                onChange={(value) => setForm((f) => ({ ...f, practical_strategies: value }))}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              onClick={saveConcern}
              disabled={saving}
              style={{ backgroundColor: "#313E6C" }}
            >
              {saving ? "Saving…" : editingId ? "Save Changes" : "Add Concern"}
            </Button>
            {editingId && (
              <Button variant="outlined" onClick={cancelEdit}>
                Cancel
              </Button>
            )}
          </Box>
        </Box>

        <Box sx={cardSx}>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 16,
            }}
          >
            <h2 style={{ color: "#313E6C", margin: 0 }}>Concerns</h2>
            <div style={{ display: "flex", gap: 8 }}>
              <TextField
                size="small"
                placeholder="New category name"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addCategory()}
              />
              <Button variant="outlined" onClick={addCategory}>
                Add Category
              </Button>
            </div>
          </div>

          {categories.map((cat) => (
            <Box
              key={cat.id}
              sx={{ border: "1px solid #eef0f4", borderRadius: "10px", p: 2, mb: 2 }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <strong style={{ color: "#313E6C" }}>
                  {cat.category}{" "}
                  <span style={{ color: "#999", fontWeight: 400, fontSize: 13 }}>
                    ({(cat.concerns || []).length})
                  </span>
                </strong>
                <Button size="small" color="error" onClick={() => deleteCategory(cat)}>
                  Delete category
                </Button>
              </div>

              {(cat.concerns || []).map((c) => (
                <div
                  key={c.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "6px 0",
                    borderTop: "1px solid #f3f4f6",
                    fontSize: 14,
                  }}
                >
                  <div>
                    {c.concern}
                    {c.name_for_report && c.name_for_report !== c.concern && (
                      <span style={{ color: "#696984" }}> → {c.name_for_report}</span>
                    )}
                    {!c.for_report && (
                      <span style={{ color: "#b45309", fontSize: 12 }}> · hidden in report</span>
                    )}
                    {!c.practical_strategies && (
                      <span style={{ color: "#dc2626", fontSize: 12 }}> · no strategies</span>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 4 }}>
                    <Button size="small" onClick={() => editConcern(c)}>
                      Edit
                    </Button>
                    <Button size="small" color="error" onClick={() => deleteConcern(c)}>
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </Box>
          ))}

          {!categories.length && (
            <p style={{ color: "#999", fontSize: 14 }}>No concern categories yet.</p>
          )}
        </Box>
      </div>
    </div>
  );
};

export default Concerns;
